import { useEffect, useRef, useState } from "react";
import { FileTypes, KindOfOrder } from "../../../../types/enums";
import './FileAttachmentUploader.scss'
import { useGetFileTypes } from "../api/get-file-types";
import Box, { Item } from "devextreme-react/box";
import Field from "../../../../layouts/field/field";
import TextBox from "devextreme-react/text-box";
import SelectBox from "devextreme-react/select-box";
import FileUploader from "devextreme-react/file-uploader";
import { showErrorMessage } from "../../../../utils/messages";
import { apiGetUploadUrl } from "../api/get-upload-url";
import { apiAddFileAttachment } from "../api/add-file-attachment";
import { ProgressBar } from "devextreme-react/progress-bar";
import { formatNumber } from "devextreme/localization";
import axios from "axios";

export default function FileAttachmentUploader(props:
	{
		orderID?: number,
		parentGuid: string,
		kindOfOrder: KindOfOrder,
		orderTypeID?: number,
		defaultFileType?: FileTypes,
		fileTypeFilter?: (fileType: FileTypes) => boolean,
		useFileTypes: boolean,
		replaceFile?: boolean,
		visible: boolean,
		onFileSelected: (fileType: FileTypes) => void,
		onFileAdded: (fileType: FileTypes) => void,
		onProcessing: (processing: boolean) => void
	}) {
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [uploadInfos, setUploadInfos] = useState<Array<{ uploadUrl: string, key: string, contentType: string }>>([]);
	const [fileType, setFileType] = useState(props.defaultFileType);
	const [progresses, setProgresses] = useState<number[]>([]);
	const [isProgressVisible, setProgressVisible] = useState(false);

	const fileTypes = useGetFileTypes(props.kindOfOrder, props.orderTypeID, props.visible && props.useFileTypes);

	useEffect(() => {
		if (!props.visible) {
			setSelectedFiles([]);
			setUploadInfos([]);
			setProgresses([]);
		} else {
			if (fileTypes.data && fileTypes.data.length > 0 &&
				(!fileType || !fileTypes.data.find((x) => x.fileTypeID === fileType))) {
				setFileType(fileTypes.data[0]?.fileTypeID);
			}
		}
	}, [fileTypes])

	useEffect(() => {
		if (!selectedFiles || selectedFiles.length === 0) {
			fileUploaderRef.current?.instance.reset();
		}

	}, [selectedFiles]);

	const fileUploaderRef = useRef<FileUploader>(null);

	const uploadSingleFile = async (file: File, uploadInfo: { uploadUrl: string, key: string, contentType: string }, idx: number) => {
		try {
			await axios.put(uploadInfo.uploadUrl, file, {
				headers: {
					"Content-Type": uploadInfo.contentType
				},
				onUploadProgress: (e) => {
					setProgresses(prev => {
						const next = [...prev];
						next[idx] = e.loaded;
						return next;
					});
				}
			});
			const result = await apiAddFileAttachment({
				orderID: props.orderID,
				parentGuid: props.parentGuid,
				kindOfOrder: props.kindOfOrder,
				fileType: fileType!,
				fileName: file.name,
				awsS3Key: uploadInfo.key,
				replaceFile: props.replaceFile ?? false
			});
			if (result.isSuccess) {
				props.onFileAdded(fileType!);
			} else {
				showErrorMessage(result.errorMessage!);
			}
		} catch (e: any) {
			showErrorMessage(e.message);
		}
	};

	let uploadInProgress = false;
	const handleUploadAll = async () => {
		if (uploadInProgress) return;
		uploadInProgress = true;
		props.onProcessing(true);
		setProgressVisible(true);
		for (let i = 0; i < selectedFiles.length; i++) {
			await uploadSingleFile(selectedFiles[i], uploadInfos[i], i);
		}
		setSelectedFiles([]);
		setUploadInfos([]);
		setProgresses([]);
		setProgressVisible(false);
		props.onProcessing(false);
		uploadInProgress = false;
	};

	return (
		<Box className="file-attachment-uploader">
			<Item ratio={1}>
				{
					props.useFileTypes &&
					<Field label="File Type">
						<SelectBox
							dataSource={props.fileTypeFilter ?
								fileTypes.data.filter(x => props.fileTypeFilter!(x.fileTypeID)) : fileTypes.data}
							valueExpr={'fileTypeID'}
							displayExpr={'fileTypeName'}
							value={fileType}
							onValueChanged={(e) => {
								if (selectedFiles.length > 0) {
									props.onFileSelected(e.value);
								}
								setFileType(e.value);
							}}>
						</SelectBox>
					</Field>
				}
				<Field label="Files">
					<TextBox value={selectedFiles.map(f => f.name).join(", ")} readOnly={true} />
				</Field>
			</Item>
			<Item ratio={0} baseSize={450}>
				<FileUploader
					ref={fileUploaderRef}
					selectButtonText="Browse Files..."
					labelText="Drag & Drop or"
					uploadMode="useButtons"
					showFileList={false}
					multiple={true}
					onValueChanged={async (e) => {
						if (e.value && e.value.length > 0) {
							const files: File[] = e.value;
							for (const file of files) {
								if (file.size > 52428800) {
									showErrorMessage("File is too large. Maximum file size allowed is 50Mb.");
									setSelectedFiles([]);
									setUploadInfos([]);
									setProgresses([]);
									return;
								}
							}
							props.onProcessing(true);
							const uploadInfoResults = await Promise.all(files.map(f => apiGetUploadUrl(f.name)));
							const validResults = uploadInfoResults.every(r => r.isSuccess);
							if (!validResults) {
								showErrorMessage("Failed to get upload URL for one or more files.");
								setSelectedFiles([]);
								setUploadInfos([]);
								setProgresses([]);
								props.onProcessing(false);
								return;
							}
							setSelectedFiles(files);
							setUploadInfos(uploadInfoResults.map(r => ({ uploadUrl: r.uploadUrl!, key: r.key!, contentType: r.contentType! })));
							setProgresses(files.map(() => 0));
							props.onProcessing(false);
							if (fileType)
								props.onFileSelected(fileType);
						}
					}}
					onUploadStarted={handleUploadAll}
				/>
			</Item>
			<Item ratio={1} baseSize={0}>
				<div className="progressbar-item-fullwidth">
					{isProgressVisible && selectedFiles.length > 0 && selectedFiles.map((file, idx) => (
						<div key={file.name} className="progressbar-row">
							<div>{file.name}</div>
							<ProgressBar
								visible={true}
								min={0}
								max={file.size}
								value={progresses[idx]}
								statusFormat={(ratio) => { return formatNumber(ratio, { type: 'percent', precision: 0 }); }}
							/>
						</div>
					))}
				</div>
			</Item>
		</Box>
	);
}

